import { NetworkStatus, useQuery } from '@apollo/client'
import { observer } from 'mobx-react'
import LazyLoad from 'react-lazy-load'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'

import Sparkline from 'components/Sparkline'
import { T, useTranslate } from 'lib/tolgee'
import AudienceFiltersStore from 'stores/AudienceFiltersStore.js'
import { getDatesInRange, humanizeLargeNumber, TOLGEE_GLOBALS, shouldBeArray } from 'utils'
import { getAudienceValuesFromObject } from 'utils/getAudienceValuesFromObject.js'

import { GET_TABLE_TERRITORY_RELEVANCE } from './query.js'

const DEFAULT_AUDIENCE_FILTERS = {
  ages: [],
  countries: [],
  genders: [],
}

function relevanceSparklineTooltip({ series, seriesIndex, dataPointIndex, w }) {
  return `
    <div class="tooltip-relevance-index">
      <span>
        ${w.globals.categoryLabels[dataPointIndex]}: ${humanizeLargeNumber(series[seriesIndex][dataPointIndex])}
      </span>
    </div>
  `
}

function RelevanceTableCell({ row }) {
  const territoryId = row?.territoryId
  const t = useTranslate()
  const audienceFilters = AudienceFiltersStore.getAudienceVariables({ countryKey: 'countries' })
  const audienceFromRow = row?.audience
  const audienceObject = getAudienceValuesFromObject(audienceFromRow)
  const audienceForQuery = {
    ages: shouldBeArray(audienceObject?.ages),
    countries: shouldBeArray(audienceObject?.countries),
    genders: shouldBeArray(audienceObject?.genders),
  }

  const finalAudienceObject = audienceFromRow ? audienceForQuery : audienceFilters

  const audienceFiltersWithDefaults = { ...DEFAULT_AUDIENCE_FILTERS, ...finalAudienceObject }

  const { error, refetch, data, networkStatus } = useQuery(GET_TABLE_TERRITORY_RELEVANCE, {
    skip: !territoryId,
    variables: {
      territoryId,
      ...audienceFiltersWithDefaults,
    },
    notifyOnNetworkStatusChange: true,
  })

  const isLoading = [NetworkStatus.loading, NetworkStatus.refetch, NetworkStatus.setVariables].includes(networkStatus)

  if (isLoading) {
    return (
      <SkeletonTheme baseColor="var(--color_neutral_400)" highlightColor="var(--color_neutral_200)">
        <Skeleton height={10} borderRadius={16} width={80} />
      </SkeletonTheme>
    )
  }

  if (error) {
    return (
      <button onClick={refetch}>
        <T keyName={TOLGEE_GLOBALS.tryAgain.key}>{TOLGEE_GLOBALS.tryAgain.defaultValue}</T>
      </button>
    )
  }

  const relevance = data?.getTerritory?.territoryPerformance?.relevancev2
  const categories = getDatesInRange(relevance?.startDate, relevance?.endDate, 'MMM/YYYY', 'month')

  return (
    <Sparkline
      data={relevance?.sparkline}
      label="Relevance"
      categories={categories}
      subtitle={`${humanizeLargeNumber(relevance?.total)} Engag.`}
      customTooltip={relevanceSparklineTooltip}
      subtitleTooltip={t(TOLGEE_GLOBALS.totalEngagement)}
    />
  )
}

const RelevanceTableCellOberver = observer(RelevanceTableCell)

export function RelevanceTableCellLazy({ row }) {
  return (
    <LazyLoad>
      <RelevanceTableCellOberver row={row} />
    </LazyLoad>
  )
}
